import React, { useState, useEffect, useMemo } from 'react';
// import { appInsights, reactPlugin } from '../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { SelectChangeEvent } from '@mui/material';
import { OptionProps, SelectProps, TransformationInputParamsConfig } from './../../view-models';
import { TextComponent, SelectComponent } from '../uiComponents';
import Button from '@mui/material/Button';
import { gritComponentStyles } from './gritComponentStyles';
import CONSTANTS from '../../constants/constants';
import {
  getTransformationConfig,
  getClaimsDropdownList,
  getClaimTransformationsDropdownList,
  getClaimTransformations,
} from '../../store';
import { useSelector } from 'react-redux';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { init_ClaimsTransformation } from '../../xmlParsing/initInterfaces';
import { Transformation, TransformationInputParams, ClaimsTransformation } from '../../xmlParsing/interfaces';
import isEqual from 'lodash/isEqual';

interface ClaimTransformationComponentProps {
  data: ClaimsTransformation;
  list: string[];
  mode: 'edit' | 'add';
  changeHandler: any;
}
interface InputParameterProps {
  id: string;
  dataType: string;
  value?: string;
  exampleValue?: string;
  options?: string[];
}

function ClaimTransformationComponent({ data, list = [], mode, changeHandler }: ClaimTransformationComponentProps) {
  const [origData, setOrigData] = useState<ClaimsTransformation>(init_ClaimsTransformation());
  const [modifiedData, setModifiedData] = useState<ClaimsTransformation>(init_ClaimsTransformation());
  const [configData, setConfigData] = useState<any>({});
  const [exampleData, setExampleData] = useState<any>({});
  const [transformationMethod, setTransformationMethod] = useState<OptionProps[]>([]);
  const [hasError, setError] = useState<boolean>(false);
  const [claimTransError, setClaimTransError] = useState<string>('Should not be empty');
  const [newClaimSelection, setNewClaimSelection] = useState<string>('selectExisting');
  const [filteredList, setFilteredList] = useState<OptionProps[]>([]);
  const transformationConfig = useSelector(getTransformationConfig);
  const allClaimsDropdownList = useSelector(getClaimsDropdownList);
  const allClaimTransformationsDropdownList = useSelector(getClaimTransformationsDropdownList);
  const allClaimTransformations = useSelector(getClaimTransformations);

  useEffect(() => {
    if (mode === 'edit') isEqual(origData, modifiedData) ? setError(true) : setError(false);
  }, [modifiedData]);

  useEffect(() => {
    if (mode === 'edit') {
      setModifiedData(data);
      setOrigData(data);
      const _configData = transformationConfig[data['transformationmethod']];
      if (
        _configData && // Without this check missing transformation configs crash the editor when clicked
        // @ts-ignore
        _configData[CONSTANTS.INPUT_CLAIMS].length === data[CONSTANTS.INPUT_CLAIMS].length &&
        // @ts-ignore
        _configData[CONSTANTS.INPUT_PARAMETERS].length === data[CONSTANTS.INPUT_PARAMETERS].length &&
        // @ts-ignore
        _configData[CONSTANTS.OUTPUT_CLAIMS].length === data[CONSTANTS.OUTPUT_CLAIMS].length
      ) {
        setConfigData(_configData);
        generateExampleData(_configData);
      } else {
        setConfigData({});
      }
    }
  }, [data, transformationConfig, mode]);

  useEffect(() => {
    if (mode === 'add' && allClaimTransformationsDropdownList.length > 0) {
      setModifiedData(init_ClaimsTransformation());
      let _temp: OptionProps[] = allClaimTransformationsDropdownList
        .filter((el: OptionProps) => !list.includes(el.value))
        .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
      setFilteredList(_temp);
      _temp.length === 0 ? setError(true) : setError(false);
      if (newClaimSelection === 'selectExisting') {
      }
      if (newClaimSelection === 'addNew') {
        const _firstOption = Object.keys(transformationConfig)[0];
        setError(true);
        setTransformationMethodTemplate(_firstOption, '');
        setConfigData(transformationConfig[_firstOption]);
        generateExampleData(transformationConfig[_firstOption]);
      }
    }
  }, [mode, newClaimSelection, list]);

  useEffect(() => {
    if (filteredList.length > 0 && mode === 'add' && newClaimSelection === 'selectExisting') {
      setModifiedData({ ...modifiedData, id: filteredList[0].value });
    }
  }, [filteredList]);

  useEffect(() => {
    if (Object.keys(transformationConfig).length > 0) {
      setTransformationMethod(
        Object.keys(transformationConfig).reduce(
          (acc: OptionProps[], curVal: string) => acc.concat([{ value: curVal, label: curVal }]),
          [],
        ),
      );
    }
  }, [transformationConfig]);

  const generateExampleData = (data: any) => {
    const _temp: any = {};
    if (data.hasOwnProperty(CONSTANTS.INPUT_CLAIMS) && data[CONSTANTS.INPUT_CLAIMS].length > 0) {
      const list: any = [];
      data['inputClaimExample'] &&
        data[CONSTANTS.INPUT_CLAIMS].forEach((inp: string, index: number) => {
          list.push({ id: inp, val: data['inputClaimExample'][index] });
        });
      _temp[CONSTANTS.INPUT_CLAIMS] = list;
    }
    if (data.hasOwnProperty(CONSTANTS.OUTPUT_CLAIMS) && data[CONSTANTS.OUTPUT_CLAIMS].length > 0) {
      const list: any = [];
      data['outputClaimExample'] &&
        data[CONSTANTS.OUTPUT_CLAIMS].forEach((inp: string, index: number) => {
          list.push({ id: inp, val: data['outputClaimExample'][index] });
        });
      _temp[CONSTANTS.OUTPUT_CLAIMS] = list;
    }
    if (data.hasOwnProperty(CONSTANTS.INPUT_PARAMETERS) && data[CONSTANTS.INPUT_PARAMETERS].length > 0) {
      const list: any = [];
      data[CONSTANTS.INPUT_PARAMETERS].forEach((d: any, index: number) => {
        list.push({ id: d.id, val: d.exampleValue || '' });
      });
      _temp[CONSTANTS.INPUT_PARAMETERS] = list;
    }
    setExampleData(_temp);
  };

  const setTransformationMethodTemplate = (value: string, id: string) => {
    setModifiedData({
      id: id,
      transformationmethod: value,
      inputClaims: transformationConfig[value]['inputClaims'].reduce(
        (acc: Transformation[], curVal: string) =>
          acc.concat([
            {
              claimReferenceId: allClaimsDropdownList[0]['value'],
              transformationClaimType: curVal,
            },
          ]),
        [],
      ),
      inputParameters: transformationConfig[value]['inputParameters'].reduce(
        (acc: InputParameterProps[], curVal: InputParameterProps) =>
          acc.concat([
            {
              id: curVal['id'],
              dataType: curVal['dataType'],
              value:
                curVal.hasOwnProperty('options') && curVal.options && curVal.options.length > 0
                  ? curVal['options'][0]
                  : '',
            },
          ]),
        [],
      ),
      outputClaims: transformationConfig[value]['outputClaims'].reduce(
        (acc: Transformation[], curVal: string) =>
          acc.concat([
            {
              claimReferenceId: allClaimsDropdownList[0]['value'],
              transformationClaimType: curVal,
            },
          ]),
        [],
      ),
    });
  };

  const classes = gritComponentStyles();
  return (
    <>
      {mode === 'add' || Object.keys(configData).length > 0 ? (
        <>
          {mode === 'add' && (
            <div id="add-claim">
              <FormControl>
                <RadioGroup
                  row
                  value={newClaimSelection}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setNewClaimSelection(e.target.value);
                  }}
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                >
                  <FormControlLabel
                    value="selectExisting"
                    control={<Radio />}
                    label="Select from existing ClaimTransformation"
                  />
                  <FormControlLabel value="addNew" control={<Radio />} label="Add a new ClaimTransformation" />
                </RadioGroup>
              </FormControl>
              <hr />
            </div>
          )}
          {modifiedData && Object.keys(transformationConfig).length > 0 && (
            <div className={classes.flexCol}>
              <div>
                {mode === 'edit' && (
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {}}
                    data={{
                      disabled: true,
                      value: modifiedData.id || '',
                      label: 'ClaimTransformation',
                    }}
                  />
                )}
                {mode === 'add' && newClaimSelection === 'selectExisting' && (
                  <>
                    {filteredList.length > 0 && (
                      <SelectComponent
                        changeHandler={(e: SelectChangeEvent<string>) => {
                          setModifiedData({ ...modifiedData, id: e.target.value });
                        }}
                        data={{
                          value: modifiedData['id'] || filteredList[0]['value'],
                          label: 'Select ClaimTransformation',
                          options: filteredList,
                        }}
                      />
                    )}
                    {filteredList.length === 0 && (
                      <div>There are no existing claim transformations. Please add a new claimTransformation.</div>
                    )}
                  </>
                )}
                {mode === 'add' && newClaimSelection === 'addNew' && (
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                      setModifiedData({
                        ...modifiedData,
                        id: e.target.value,
                      });
                      if (e.target.value === '') {
                        setError(true);
                        setClaimTransError('Should not be empty');
                      } else if (!/^[a-zA-Z0-9]+[a-zA-Z0-9_-]{0,}$/.test(e.target.value)) {
                        setClaimTransError(
                          'Should begin with an alphabet or number and can contain alphabets, numbers and the following symbols: _ -',
                        );
                      } else if (
                        Object.keys(allClaimTransformations).findIndex(
                          (el: string) => el.trim().toLowerCase() === e.target.value.trim().toLowerCase(),
                        ) >= 0
                      ) {
                        setError(true);
                        setClaimTransError('A claimTransformation exists with the same Id');
                      } else {
                        setError(false);
                        setClaimTransError('');
                      }
                    }}
                    data={{
                      value: modifiedData['id'],
                      label: 'New Claim Transformation Id',
                      style: { margin: 1, width: 400 },
                      error: !!claimTransError,
                      helperText: claimTransError,
                    }}
                  />
                )}
              </div>
              {(mode === 'edit' || (mode === 'add' && newClaimSelection === 'addNew')) && (
                <div className={classes.flexRow}>
                  <div className={classes.claimsTransformationLeft}>
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        setConfigData(transformationConfig[e.target.value]);
                        setTransformationMethodTemplate(e.target.value, modifiedData['id']);
                        generateExampleData(transformationConfig[e.target.value]);
                      }}
                      data={{
                        value: modifiedData['transformationmethod'],
                        label: 'TransformationMethod',
                        options: transformationMethod,
                      }}
                    />
                    {Object.keys(configData).length > 0 && (
                      <>
                        <h3>Input Claims</h3>
                        <div className={classes.flexCol}>
                          {configData['inputClaims'].map((inp: string, index: number) => {
                            return (
                              <div key={'ipclaim-' + index + Math.random() * 10000}>
                                <SelectComponent
                                  changeHandler={(e: SelectChangeEvent<string>) => {
                                    setModifiedData({
                                      ...modifiedData,
                                      inputClaims: modifiedData['inputClaims'].reduce(
                                        (acc: Transformation[], curVal: Transformation, curInd: number) =>
                                          curInd === index
                                            ? acc.concat([
                                                {
                                                  ...modifiedData['inputClaims'][index],
                                                  claimReferenceId: e.target.value,
                                                },
                                              ])
                                            : acc.concat([curVal]),
                                        [],
                                      ),
                                    });
                                  }}
                                  data={{
                                    value:
                                      (modifiedData.hasOwnProperty('inputClaims') &&
                                        modifiedData['inputClaims'].length > 0 &&
                                        modifiedData['inputClaims'][index]['claimReferenceId']) ||
                                      allClaimsDropdownList[0]['value'],
                                    label: inp,
                                    options: allClaimsDropdownList,
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>

                        <h3>Input Parameters </h3>
                        {configData['inputParameters'].map((param: TransformationInputParamsConfig, index: number) => {
                          if (param.hasOwnProperty('options') && param['options'].length > 0) {
                            return (
                              <div key={'param-' + Math.random() * 10000}>
                                <SelectComponent
                                  changeHandler={(e: SelectChangeEvent<string>) => {
                                    setModifiedData({
                                      ...modifiedData,
                                      inputParameters: modifiedData['inputParameters'].reduce(
                                        (
                                          acc: TransformationInputParams[],
                                          curVal: TransformationInputParams,
                                          curInd: number,
                                        ) =>
                                          curInd === index
                                            ? acc.concat([
                                                { ...modifiedData['inputParameters'][index], value: e.target.value },
                                              ])
                                            : acc.concat([curVal]),
                                        [],
                                      ),
                                    });
                                  }}
                                  data={{
                                    value:
                                      (modifiedData.hasOwnProperty('inputParameters') &&
                                        modifiedData['inputParameters'].length > 0 &&
                                        modifiedData['inputParameters'][index]['value']) ||
                                      param['options'][0],
                                    label: param['id'],
                                    options: param['options'].reduce(
                                      (acc: OptionProps[], curVal: string) =>
                                        acc.concat([{ value: curVal, label: curVal }]),
                                      [],
                                    ),
                                  }}
                                />
                              </div>
                            );
                          } else {
                            return (
                              <div key={'param-' + Math.random() * 10000}>
                                <TextComponent
                                  changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                                    setModifiedData({
                                      ...modifiedData,
                                      inputParameters: modifiedData['inputParameters'].reduce(
                                        (
                                          acc: TransformationInputParams[],
                                          curVal: TransformationInputParams,
                                          curInd: number,
                                        ) =>
                                          curInd === index
                                            ? acc.concat([
                                                { ...modifiedData['inputParameters'][index], value: e.target.value },
                                              ])
                                            : acc.concat([curVal]),
                                        [],
                                      ),
                                    });
                                  }}
                                  data={{
                                    value:
                                      (modifiedData.hasOwnProperty('inputParameters') &&
                                        modifiedData['inputParameters'].length > 0 &&
                                        modifiedData['inputParameters'][index]['value']) ||
                                      '',
                                    label: param['id'],
                                  }}
                                />
                              </div>
                            );
                          }
                        })}

                        <h3>Output Claims</h3>
                        <div className={classes.flexRow}>
                          {configData['outputClaims'].map((out: string, index: number) => {
                            return (
                              <div key={'opclaim-' + index + Math.random() * 10000}>
                                <SelectComponent
                                  changeHandler={(e: SelectChangeEvent<string>) => {
                                    setModifiedData({
                                      ...modifiedData,
                                      outputClaims: modifiedData['outputClaims'].reduce(
                                        (acc: Transformation[], curVal: Transformation, curInd: number) =>
                                          curInd === index
                                            ? acc.concat([
                                                {
                                                  ...modifiedData['outputClaims'][index],
                                                  claimReferenceId: e.target.value,
                                                },
                                              ])
                                            : acc.concat([curVal]),
                                        [],
                                      ),
                                    });
                                  }}
                                  data={{
                                    value:
                                      (modifiedData.hasOwnProperty('outputClaims') &&
                                        modifiedData['outputClaims'].length > 0 &&
                                        modifiedData['outputClaims'][index]['claimReferenceId']) ||
                                      allClaimsDropdownList[0]['value'],
                                    label: out,
                                    options: allClaimsDropdownList,
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                  {modifiedData['transformationmethod'] && (
                    <div className={classes.transformationExample}>
                      <h2>Example for {modifiedData['transformationmethod']}</h2>
                      <>{exampleData.hasOwnProperty(CONSTANTS.INPUT_CLAIMS) && <h3>Input Claims</h3>}</>
                      <>
                        {exampleData.hasOwnProperty(CONSTANTS.INPUT_CLAIMS) &&
                          exampleData[CONSTANTS.INPUT_CLAIMS].map((el: any) => (
                            <p key={'id-' + Math.random() * 1000}>
                              <b>{el.id} :</b>&nbsp;&nbsp;&nbsp;
                              {el.val}
                            </p>
                          ))}
                      </>
                      <>{exampleData.hasOwnProperty(CONSTANTS.INPUT_PARAMETERS) && <h3>Input Parameters</h3>}</>
                      <>
                        {exampleData.hasOwnProperty(CONSTANTS.INPUT_PARAMETERS) &&
                          exampleData[CONSTANTS.INPUT_PARAMETERS].map((el: any) => (
                            <p key={'id-' + Math.random() * 1000}>
                              <b>{el.id} :</b>&nbsp;&nbsp;&nbsp;
                              {el.val}
                            </p>
                          ))}
                      </>
                      <>{exampleData.hasOwnProperty(CONSTANTS.OUTPUT_CLAIMS) && <h3>Output Claims</h3>}</>
                      <>
                        {exampleData.hasOwnProperty(CONSTANTS.OUTPUT_CLAIMS) &&
                          exampleData[CONSTANTS.OUTPUT_CLAIMS].map((el: any) => (
                            <p key={'id-' + Math.random() * 1000}>
                              <b>{el.id} :</b>&nbsp;&nbsp;&nbsp;
                              {el.val}
                            </p>
                          ))}
                      </>
                    </div>
                  )}
                </div>
              )}
              <footer className={classes.footer}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={hasError}
                  onClick={() => {
                    changeHandler(modifiedData);
                  }}
                >
                  Submit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    changeHandler(null);
                  }}
                >
                  Cancel
                </Button>
              </footer>
            </div>
          )}
        </>
      ) : (
        <h3> XML has some issue in transformation method {modifiedData['id']}</h3>
      )}
    </>
  );
}

// export default withAITracking(reactPlugin, ClaimTransformationComponent);
export default ClaimTransformationComponent;
